import React, { useState } from 'react';
import { Mail, User, Send, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/ApiService';

export const ContactPage: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: 'General Inquiry',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        // Real API call
        try {
            await apiService.submitContactForm(formData);
            setSubmitted(true);
            setFormData({ name: '', email: '', subject: 'General Inquiry', message: '' });
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="max-w-2xl mx-auto py-12 px-4">
                <div className="glass-card p-8 text-center animate-in fade-in zoom-in duration-500">
                    <div className="flex justify-center mb-6">
                        <div className="p-4 bg-success-100 rounded-full">
                            <CheckCircle className="w-12 h-12 text-success-600" />
                        </div>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Message Sent!</h2>
                    <p className="text-gray-600 mb-8">
                        Thank you for reaching out. We've received your message and will get back to you as soon as possible.
                    </p>
                    <Link to="/" className="btn-primary inline-flex items-center">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
                <Link to="/" className="text-primary-600 hover:text-primary-700 flex items-center font-medium transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Home
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
                            Get in <span className="text-gradient-primary">Touch</span>
                        </h1>
                        <p className="text-lg text-gray-600">
                            Have questions about web accessibility or our scanner? We're here to help you make the web more inclusive for everyone.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-start space-x-4">
                            <div className="p-3 bg-primary-100 rounded-xl">
                                <Mail className="w-6 h-6 text-primary-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">Email Us</h3>
                                <p className="text-gray-600">support@a11yreport.com</p>
                            </div>
                        </div>

                        <div className="bg-primary-50 p-6 rounded-2xl border border-primary-100">
                            <h3 className="font-semibold text-primary-900 mb-2">Our Mission</h3>
                            <p className="text-sm text-primary-800 leading-relaxed">
                                We believe accessibility is a fundamental human right. Our tools are designed to help developers and businesses identify and fix barriers that prevent people with disabilities from fully participating in the digital world.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="glass-card p-8">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="name" className="form-label">Full Name</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="form-input pl-10"
                                    placeholder="John Doe"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="email" className="form-label">Email Address</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="form-input pl-10"
                                    placeholder="john@example.com"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="subject" className="form-label">Subject</label>
                            <select
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                className="form-input"
                            >
                                <option>General Inquiry</option>
                                <option>Technical Support</option>
                                <option>Feature Request</option>
                                <option>Compliance Question</option>
                                <option>Other</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="message" className="form-label">Message</label>
                            <textarea
                                id="message"
                                name="message"
                                required
                                rows={4}
                                value={formData.message}
                                onChange={handleChange}
                                className="form-input resize-none"
                                placeholder="How can we help you?"
                            ></textarea>
                        </div>

                        {error && (
                            <div className="p-3 bg-error-50 text-error-700 text-sm rounded-lg border border-error-100">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-primary w-full py-3 text-lg font-semibold flex items-center justify-center space-x-2 shadow-lg shadow-primary-200"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="loading-spinner h-5 w-5 !border-white mr-2" />
                                    <span>Sending...</span>
                                </>
                            ) : (
                                <>
                                    <Send className="w-5 h-5 mr-2" />
                                    <span>Send Message</span>
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};
